import { TypedDocumentNode, gql } from '@apollo/client';
import { faChartLine, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  ManagerProgressionCategorySlug,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { isType } from '@sorare/core/src/gql';
import { glossary } from '@sorare/core/src/lib/glossary';
import { Color } from '@sorare/core/src/style/types';
import { createVar } from '@sorare/core/src/style/utils';

import { Block } from '../Block';
import { ProfileCta } from '../ProfileCta';
import { Challenges_ManagerProgression } from './__generated__/index.graphql';

const messages = defineMessages({
  seeChallenges: {
    id: 'ManagerProfile.MyProgression.seeChallenges',
    defaultMessage: 'See Challenges',
  },
});

const progressColor = createVar();

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--intermediate-unit);
`;
const ProgressContainer = styled.div`
  text-align: center;
  position: relative;
`;
const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  width: var(--double-unit);
  height: var(--double-unit);

  position: absolute;
  top: calc(50% - var(--double-unit) / 2);
  left: calc(50% - var(--double-unit) / 2);
`;
const StyledCircularProgress = styled(CircularProgress)`
  width: var(--quadruple-unit);
  margin: 0 auto;

  --circular-progress-stroke: var(${progressColor});
`;
const StyledBodyS = styled(BodyS)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Count = styled(BodyS)`
  background: var(--c-nd-100);
  border-radius: 50px;
  padding: 0 var(--unit);
`;

export const thresholds = [
  [0.1, 'veryLow'],
  [0.2, 'low'],
  [0.4, 'mediumLow'],
  [0.6, 'medium'],
  [0.8, 'mediumHigh'],
] as const;

const THRESHOLD_COLORS: { [key: string]: Color } = {
  veryLow: 'var(--c-score-veryLow)',
  low: 'var(--c-score-low)',
  mediumLow: 'var(--c-score-mediumLow)',
  medium: 'var(--c-score-medium)',
  mediumHigh: 'var(--c-score-mediumHigh)',
  high: 'var(--c-score-high)',
};

const findThreshold = (score: number) => {
  const threshold = thresholds.find(t => Number(t[0]) > score);

  return threshold ? threshold[1] : 'high';
};

type Props = {
  challengesUrl: string;
  managerProgression: Challenges_ManagerProgression;
  showUnclaimedBadge: boolean;
};

export const Challenges = ({
  challengesUrl,
  managerProgression,
  showUnclaimedBadge,
}: Props) => {
  const challengesClaimedPerCategory = (
    managerProgression.categories ?? []
  ).reduce((acc, category) => {
    return {
      ...acc,
      [category.name]: {
        category,
        count:
          category.categoryTasks.filter(
            task =>
              isType(task, 'ManagerProgressionTask') &&
              task.aasmState === ManagerProgressionTaskState.CLAIMED
          ).length ?? 0,
      },
    };
  }, {}) as Record<
    ManagerProgressionCategorySlug,
    {
      category: NonNullable<
        Challenges_ManagerProgression['categories']
      >[number];
      count: number;
    }
  >;

  const totalChallengesClaimed = Object.values(
    challengesClaimedPerCategory
  ).reduce((acc, { count }) => acc + count, 0);

  const unclaimedChallenges = showUnclaimedBadge
    ? (managerProgression.categories || []).flatMap(category =>
        category.categoryTasks
          .map(
            t =>
              isType(t, 'ManagerProgressionTask') &&
              t.aasmState === ManagerProgressionTaskState.COMPLETED
          )
          .filter(Boolean)
      ).length
    : 0;

  return (
    <Block
      title={<FormattedMessage {...glossary.challenges} />}
      icon={<FontAwesomeIcon icon={faChartLine} />}
      cta={
        <ProfileCta to={challengesUrl}>
          <Badge badgeContent={unclaimedChallenges}>
            <FormattedMessage {...messages.seeChallenges} />
          </Badge>
        </ProfileCta>
      }
      header={
        <Horizontal center>
          <BodyS as="p">
            <FormattedNumber value={totalChallengesClaimed} />
          </BodyS>
          <BodyS as="p" color="var(--c-nd-600)">
            <FormattedMessage
              id="Manager.Challenges.challengedClaimed"
              defaultMessage="{count, plural, =0 {Challenge} one {Challenge} other {Challenges}} claimed"
              values={{ count: totalChallengesClaimed }}
            />
          </BodyS>
        </Horizontal>
      }
    >
      <Grid>
        {Object.values(challengesClaimedPerCategory)
          .sort(({ count: count1 }, { count: count2 }) => count2 - count1)
          .slice(0, 4)
          .map(({ category, count }) => {
            const tasks = category.categoryTasks.filter(task =>
              isType(task, 'ManagerProgressionTask')
            );

            return (
              <Vertical
                key={category.name}
                style={{
                  [progressColor]:
                    THRESHOLD_COLORS[findThreshold(count / tasks.length)],
                }}
              >
                <ProgressContainer>
                  <StyledCircularProgress
                    progress={count}
                    maxProgress={tasks.length}
                    strokeWidth={16}
                  />
                  {count === tasks.length && (
                    <StyledFontAwesomeIcon
                      icon={faCheck}
                      color={THRESHOLD_COLORS.high}
                    />
                  )}
                </ProgressContainer>
                <StyledBodyS
                  as="p"
                  color="var(--c-nd-600)"
                  title={category.title}
                >
                  {category.title}
                </StyledBodyS>
                <Horizontal center>
                  <Count as="span">x{count}</Count>
                </Horizontal>
              </Vertical>
            );
          })}
      </Grid>
    </Block>
  );
};

Challenges.fragments = {
  managerProgression: gql`
    fragment Challenges_ManagerProgression on ManagerProgression {
      id
      categories(type: MANAGER_PROGRESSION_CHALLENGES) {
        id
        title
        name
        categoryTasks {
          ... on ManagerTaskInterface {
            id
            aasmState
          }
        }
      }
    }
  ` as TypedDocumentNode<Challenges_ManagerProgression>,
};
